import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useRenameServiceLine } from "@/domains/service-lines/services/account-service-lines";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { ServiceLine } from "@/shared/types/serviceLineTypes";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";

interface RenameServiceLineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  serviceLine: ServiceLine;
}

const RenameServiceLineDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine,
}: RenameServiceLineDialogProps) => {
  const renameServiceLine = useRenameServiceLine();
  const [formData, setFormData] = useState<string>(serviceLine.serviceLineName ?? '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) {
      showError(ERROR.SERVICE_LINE.EDIT_NAME);
      return;
    }

    try {
      await renameServiceLine.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        serviceLineName: formData,
      });
      showSuccess(SUCCESS.SERVICE_LINE.EDITED_NAME);
      onSuccess();
      onOpenChange(false);
      // Reset form
      setFormData(null);
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.EDIT_NAME);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Service Line Name</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Input
                id="name"
                name="name"
                placeholder="Enter Service Line Name"
                value={formData}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={renameServiceLine.isPending}

            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={renameServiceLine.isPending}
              className="gap-2">
              {renameServiceLine.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Editing...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RenameServiceLineDialog; 